import { Injectable } from '@jack-henry/frontend-utils/di';
import {
    ApplicationNameType2Dto,
    ArpFileErrorDto,
    IssuedItemFileClient,
    IssuedItemFileCreatedByUserDto,
    IssuedItemFileDto,
    PositivePayCompanyConfigurationClient,
} from '@treasury/api/channel';
import { OmegaDate } from '@treasury/omega/components/datepicker';
import { Nullable } from '@treasury/utils';
import { FiDate } from '../../../dates';
import {
    createIssuedItemsFile,
    IssuedItemsFile,
} from '../../mappings/arp/issued-items-file.entity';
import { DownloadService } from '../download';

export interface SourceItem {
    id: string;
    name: string;
}

export interface IssuedItemFileSearchParams {
    fileUniqueId?: string;
    fileName?: string;
    sourceList?: SourceItem[];
    uploadedDate?: OmegaDate;
    processedDate?: OmegaDate;
}

export interface IssuedItemFileSearchResult {
    items: IssuedItemsFile[];
    totalCount: number;
    pageSize: number;
    pageNumber: number;
}

@Injectable()
export class IssuedItemFileService {
    constructor(
        private readonly issuedItemFileClient: IssuedItemFileClient,
        private readonly positivePayCompanyConfigurationClient: PositivePayCompanyConfigurationClient
    ) {}

    public async searchIssuedItemFiles(
        searchParams: IssuedItemFileSearchParams = {},
        pageSize = 10,
        pageNumber = 1
    ): Promise<IssuedItemFileSearchResult> {
        const searchRequest = {
            applicationNameType: ApplicationNameType2Dto.Banno,
            pagingParameters: {
                pageSize,
                pageNumber,
                searchBys: this.mapSearchParams(searchParams),
            },
        };

        const response =
            await this.issuedItemFileClient.issuedItemFileSearchIssuedItemFilesActivity(
                searchRequest
            );

        const items = (response.data?.items || []).map(dto =>
            createIssuedItemsFile(dto as Nullable<IssuedItemFileDto>)
        );

        return {
            items,
            totalCount: response.data?.totalCount || 0,
            pageSize: response.data?.pageSize || pageSize,
            pageNumber: response.data?.pageNumber || pageNumber,
        };
    }

    public async getFileContents(uniqueId: string): Promise<string> {
        const response =
            await this.issuedItemFileClient.issuedItemFileGetIssuedItemFileContent(uniqueId);
        return response.data?.fileContent || '';
    }

    public async getFileErrors(uniqueId: string): Promise<ArpFileErrorDto[]> {
        const response =
            await this.issuedItemFileClient.issuedItemFileGetIssuedItemFileError(uniqueId);
        return response.data?.fileErrors || [];
    }

    public async getCreatedByUsers(): Promise<IssuedItemFileCreatedByUserDto[]> {
        const response = await this.issuedItemFileClient.issuedItemFileGetCreatedByUsers();
        return response.data?.createdByUsers || [];
    }

    public async processFile(uniqueId: string): Promise<boolean> {
        const response =
            await this.issuedItemFileClient.issuedItemFileProcessIssuedItemFile(uniqueId);
        // Return true if response is successful (no errors in the response data)
        return !!(
            response.data &&
            (!response.data.fileErrors || response.data.fileErrors.length === 0)
        );
    }

    public async deleteFile(uniqueId: string): Promise<boolean> {
        const response =
            await this.issuedItemFileClient.issuedItemFileDeleteIssuedItemFile(uniqueId);
        return response.data || false;
    }

    public async downloadFileActivity(
        outputType: 'PDF' | 'CSV',
        filterModel: Record<string, unknown> = {}
    ): Promise<void> {
        const fileSuffix = new FiDate(new Date()).toIsoDate();
        const pageId = 'IssuedItemFileActivity';

        return DownloadService.download(
            fileSuffix,
            outputType,
            pageId,
            { ...filterModel, page: pageId },
            ['CSV', 'PDF']
        );
    }

    public async getCompanyPosPayConfigurationOptions(): Promise<Record<string, boolean>> {
        const response =
            await this.positivePayCompanyConfigurationClient.positivePayCompanyConfigurationGetAllOptions();

        const options: Record<string, boolean> = {};

        if (response.data) {
            response.data.forEach(item => {
                if (item.name && item.value) {
                    const cleanName = item.name.replace(/^PositivePay\./, '');
                    options[cleanName] = item.value === '1';
                }
            });
        }

        return options;
    }

    /**
     * Map search parameters directly to API search criteria
     */
    private mapSearchParams(
        searchParams: IssuedItemFileSearchParams
    ): Array<Record<string, unknown>> {
        const { uploadedDate } = searchParams;
        const searchBys: Array<Record<string, unknown>> = [];

        if (uploadedDate && 'dates' in uploadedDate && uploadedDate.dates.length > 0) {
            const [fromDate, toDate = fromDate] = uploadedDate.dates;

            searchBys.push({
                name: 'UploadedDate',
                value: this.formatDateForApi(fromDate),
                secondValue: this.formatDateForApi(toDate),
                searchType: 'Between',
            });
        }

        // The rest of the search parameters will be implemented in Phase 2 of the Issued Items SFTP project
        /* if (fileName) {
            searchBys.push({
                name: 'FileName',
                value: fileName,
                searchType: 'Contains',
            });
        }

        if (sourceList && sourceList.length > 0) {
            const [firstSource] = sourceList;
            searchBys.push({
                name: 'Source',
                value: firstSource.id,
                searchType: 'Equals',
            });
        }

        if (processedDate && 'dates' in processedDate && processedDate.dates.length > 0) {
            const [fromDate, toDate = fromDate] = processedDate.dates;

            searchBys.push({
                name: 'ProcessedDate',
                value: this.formatDateForApi(fromDate),
                secondValue: this.formatDateForApi(toDate),
                searchType: 'Between',
            });
        }

        if (fileUniqueId) {
            searchBys.push({
                name: 'FileUniqueId',
                value: fileUniqueId,
                searchType: 'Equals',
            });
        } */

        return searchBys;
    }

    private formatDateForApi(date: Date): string {
        return new FiDate(date).toIsoDate();
    }
}
